import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../services/Api/Admin/adminApi";
import { partnerApi } from "../../../../../../services/Api/Partner/partnerApi";
import { api } from "../../../../../../services/Api/api";
import {
	CoordinatesApi,
	GetCoordinatesApi,
	OrganizationApi,
	OrganizationFormProps,
} from "../../../../../../services/Api/types";
import { hasRole } from "../../../../../../services/users/selectors";
import OrganizationAdminCompanyInfoForm from "./OrganizationAdminCompanyInfoForm";
import OrganizationAdminOrganizationInfoForm from "./OrganizationAdminOrganizationInfoForm";
import OrganizationAdminOtherInfoForm from "./OrganizationAdminOtherInfoForm";

interface Props {
	organization: OrganizationApi;
	customFieldsConfig: CustomFieldTemplateApi[];
	isPartner?: boolean;
	partnerId: number | undefined;
}

const OrganizationAdminForm = ({ organization, isPartner, customFieldsConfig, partnerId }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const form = useForm<OrganizationFormProps>({
		criteriaMode: "all",
		defaultValues: {
			name: organization.name,
			contact_person: organization.contact_person,
			tags: organization.tags,
			address: {
				city: organization?.company_city,
				zip_code: organization?.company_zip_code,
				street: organization?.company_street,
				build_nr: organization?.company_build_nr,
				flat_nr: organization?.company_flat_nr,
				country: organization.company_country,
			},
			place: {
				...organization.place,
				facebook: organization?.place?.facebook?.replace("facebook.com/", ""),
				linkedin: organization?.place?.linkedin?.replace("linkedin.com/company/", ""),
				website: organization?.place?.website?.replace("https://", ""),
				instagram: organization?.place?.instagram?.replace("instagram.com/", ""),
			},
			contact: {
				phone_number: organization?.company_phone_number,
				email: organization?.company_email,
			},
			tax_id_no: organization?.company_tax_id_no,
			company_name: organization?.company_name,
			custom_fields: mapCustomFieldsToForm(customFieldsConfig, organization.custom_fields),
		},
	});
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		register,
		reset,
		formState: { errors },
		handleSubmit,
		setError,
		formState,
	} = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const hasAcceptedRules = organization.accept_rules && organization.accept_terms && organization.accept_privacy;
	const [hasOrganizationCoords, setHasOrganizationCoords] = useState<boolean>(
		!!(organization?.place?.location?.latitude && organization?.place?.location?.longitude)
	);
	const hasSupportRole = useSelector(hasRole("SUPPORT"));
	const hasOfficeRole = useSelector(hasRole("OFFICE"));
	const [coordsParams, setCoordsParams] = useState<GetCoordinatesApi>({
		city: organization?.place?.city || "",
		street: organization?.place?.street || "",
		build_nr: organization?.place?.build_nr || "",
	});
	const [organizationCoords, setOrganizationCoords] = useState<CoordinatesApi>({
		latitude: organization?.place?.location?.latitude ? organization?.place?.location?.latitude : 51.9189046,
		longitude: organization?.place?.location?.longitude ? organization?.place?.location?.longitude : 19.1343786,
	});
	const [isMounted, setIsMounted] = useState<boolean>(false);
	const [loadingCoords, setLoadingCoords] = useState<boolean>(false);
	const countryOptions = [
		{
			value: "Poland",
			label: t("enums.countries.POLAND"),
		},
	];

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (isMounted)
			setTimeout(() => {
				onFetchCoords();
			}, 100);
	}, [coordsParams]);

	const canEdit = (dataType?: "organization" | "company" | "contact") => {
		if (isPartner === false || isPartner === undefined) return true;
		if (isPartner) {
			if (hasSupportRole) {
				if (dataType === "organization" || dataType === "contact") return true;
				return false;
			}
			if (dataType === "company") return !hasAcceptedRules;
			if (dataType === "organization") return !hasAcceptedRules || hasSupportRole || hasOfficeRole;
			if (dataType === "contact") return hasSupportRole || hasOfficeRole;
		}
	};

	const onFetchCoords = async () => {
		setLoadingCoords(true);
		try {
			const res = await api.getCoordinates(coordsParams);
			if (res?.longitude !== undefined && res?.latitude !== undefined) {
				setOrganizationCoords({
					latitude: res.latitude,
					longitude: res.longitude,
				});
				setHasOrganizationCoords(true);
			}
		} catch (err) {
			setHasOrganizationCoords(false);
			setOrganizationCoords({
				latitude: 51.9189046,
				longitude: 19.1343786,
			});
		}
		setLoadingCoords(false);
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const params = { include: "place,custom_fields" };
			if (data.place.facebook) {
				data.place.facebook = `facebook.com/${data.place.facebook}`;
			}

			if (data.place.instagram) {
				data.place.instagram = `instagram.com/${data.place.instagram}`;
			}

			if (data.place.linkedin) {
				data.place.linkedin = `linkedin.com/company/${data.place.linkedin}`;
			}

			if (data.place.website) {
				data.place.website = `https://${data.place.website}`;
			}

			if (data.custom_fields) data.custom_fields.forEach((field) => (field.value ? null : (field.value = "")));

			let res;
			if (isPartner) {
				res = await partnerApi.updateOrganization(Number(partnerId), organization.id, data, params);
			} else {
				res = await adminApi.updateAdminOrganization(organization.id, data, params);
			}

			setLoading(false);
			reset({
				name: res.name,
				contact_person: res.contact_person,
				tags: res.tags,
				address: {
					city: res?.company_city,
					zip_code: res?.company_zip_code,
					street: res?.company_street,
					build_nr: res?.company_build_nr,
					flat_nr: res?.company_flat_nr,
					country: res?.company_country,
				},
				place: {
					...res.place,
					facebook: res?.place?.facebook?.replace("facebook.com/", ""),
					linkedin: res?.place?.linkedin?.replace("linkedin.com/company/", ""),
					website: res?.place?.website?.replace("https://", ""),
					instagram: res?.place?.instagram?.replace("instagram.com/", ""),
				},
				contact: {
					phone_number: res?.company_phone_number,
					email: res?.company_email,
				},
				tax_id_no: res?.company_tax_id_no,
				company_name: res?.company_name,
				custom_fields: mapCustomFieldsToForm(customFieldsConfig, res.custom_fields),
			});
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			setLoading(false);
			handleError.form(e, setError, addFlash);
		}
	});

	const getAllCountryOptions = (option: Record<string, string>) => {
		const options: Record<string, string>[] = [...countryOptions];
		if (option?.value && option?.label) options.push(option);
		return options;
	};

	return (
		<FormDirty onSubmit={onSubmit} loading={loading} formState={formState} className="organization-form">
			<OrganizationAdminCompanyInfoForm
				organization={organization}
				form={form}
				canEdit={canEdit}
				getAllCountryOptions={getAllCountryOptions}
			/>
			<OrganizationAdminOrganizationInfoForm
				organization={organization}
				form={form}
				canEdit={canEdit}
				getAllCountryOptions={getAllCountryOptions}
				organizationCoords={organizationCoords}
				hasOrganizationCoords={hasOrganizationCoords}
				coordsParams={coordsParams}
				loadingCoords={loadingCoords}
				setCoordsParams={setCoordsParams}
			/>
			<fieldset>
				<h5 className="mt-4">{t("common.word.contact_info")}</h5>
				<FormInput
					label={t("modules.organization.field.contact_person.title")}
					name={"contact_person"}
					disabled={!canEdit("contact")}
					errors={errors}
					register={register}
				/>
			</fieldset>
			<OrganizationAdminOtherInfoForm
				organization={organization}
				form={form}
				canEdit={canEdit}
				customFieldsConfig={customFieldsConfig}
				setErrors={setErrors}
				isPartner={isPartner}
			/>
		</FormDirty>
	);
};
export default OrganizationAdminForm;
